import React from 'react';
import { Card } from 'antd';
import { QuestionCircleOutlined, SendOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { format, isToday, isYesterday } from 'date-fns';
import styled, { css } from 'styled-components';
import { UserAvatar } from '../../../users/UserAvatar';
import { FormattedMessage, useIntl } from 'react-intl';

const mobile = (styles) => css`
  @media (max-width: 600px) {
    ${styles}
  }
`;

const isStudentMessageStyles = (props) => props.$isStudentMessage && css`
  justify-content: flex-end;
`;

const StyledChatCard = styled(Card)`
  border-radius: 1rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 1200px;
  margin: 0 auto;

  .ant-card-body {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    padding: 0.5rem 0.25rem !important;
  }
`;

const ScrollContainer = styled.div`
  overflow-y: auto;
  flex-grow: 1;
  min-height: 400px;
  max-height: 70vh;
  padding: 0 0.5rem;

  ${mobile(css`
    min-height: 300px;
    max-height: 80vh;
  `)}

  &::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const MessageContainer = styled.div`
  display: flex;
  margin-bottom: 24px;
  ${isStudentMessageStyles}
  position: relative;
`;

const DialogueWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  flex-direction: ${(props) => (props.$isStudentMessage ? 'row-reverse' : 'row')};
  max-width: 70%;

  ${mobile(css`
    max-width: 90%;
  `)}
`;

const AvatarWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  font-size: 20px;
`;

const MessageContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.$isStudentMessage ? 'flex-end' : 'flex-start')};
`;

const MessageBubbleWrapper = styled.div`
  position: relative;
`;

const MessageBubble = styled.div`
  padding: 12px 16px;
  border-radius: 12px;
  background-color: ${(props) => (props.$isStudentMessage ? '#1890ff' : '#f0f0f0')};
  color: ${(props) => (props.$isStudentMessage ? '#fff' : '#000')};
  white-space: pre-wrap;
  word-break: break-word;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  font-size: 1rem;

  ${mobile(css`
    padding: 10px 14px;
    font-size: 0.9rem;
  `)}
`;

const ContextIconBadge = styled.div`
  position: absolute;
  bottom: -4px;
  right: -4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  font-size: 14px;
  color: ${(props) => props.$iconColor};
`;

const Timestamp = styled.span`
  display: block;
  font-size: 0.65rem;
  color: #8c8c8c;
  text-align: ${(props) => (props.$isStudentMessage ? 'right' : 'left')};

  ${mobile(css`
    font-size: 0.55rem;
  `)}
  position: absolute;
  bottom: -1.2rem;

  ${mobile(css`
    bottom: -1rem;
  `)}

  ${(props) => (props.$isStudentMessage && 'right: 3.5rem;')}
  ${(props) => (!props.$isStudentMessage && 'left: 3.5rem')};
  width: 100%;
`;

const Dialogue = ({ message, student }) => {
  const getMessageIcon = (messageContext) => {
    switch (messageContext) {
      case 'question-statement':
        return { icon: <QuestionCircleOutlined />, color: '#1890ff' };
      case 'answer-attempt':
        return { icon: <SendOutlined />, color: '#52c41a' };
      case 'correct-answer-response':
        return { icon: <CheckCircleOutlined />, color: '#52c41a' };
      case 'incorrect-answer-response':
        return { icon: <CloseCircleOutlined />, color: '#ff4d4f' };
      default:
        return { icon: <QuestionCircleOutlined />, color: '#8c8c8c' };
    }
  };

  const formatDialogueTimestamp = (timestamp) => {
    if (!timestamp) return <FormattedMessage id="edit-student-profile.main.student-dailogue.justNow"/>;
    const date = new Date(Number(timestamp));

    if (isToday(date)) {
      return format(date, 'p');
    }
    if (isYesterday(date)) {
      return <FormattedMessage id="edit-student-profile.main.student-dailogue.justNow"/> + format(date, 'p');
    }

    return format(date, 'MMM d, p');
  };

  const isStudentMessage = message.messageContext === 'answer-attempt';
  const { icon, color } = getMessageIcon(message.messageContext);

  const formattedTimestamp = formatDialogueTimestamp(message.createdAt);

  return (
    <MessageContainer $isStudentMessage={isStudentMessage}>
      <DialogueWrapper $isStudentMessage={isStudentMessage}>
        <AvatarWrapper $isStudentMessage={isStudentMessage}>
          {isStudentMessage ? (
            <UserAvatar size={36} user={student} />
          ) : (
            <span>🤖</span>
          )}
        </AvatarWrapper>
        <MessageContent $isStudentMessage={isStudentMessage}>
          <MessageBubbleWrapper>
            <MessageBubble $isStudentMessage={isStudentMessage}>
              {message.messageBody}
            </MessageBubble>
            {
              !isStudentMessage &&
                <ContextIconBadge $iconColor={color}>
                  {icon}
                </ContextIconBadge>
            }
          </MessageBubbleWrapper>
          <Timestamp $isStudentMessage={isStudentMessage}>
            {formattedTimestamp}
          </Timestamp>
        </MessageContent>
      </DialogueWrapper>
    </MessageContainer>
  );
};

const StudentDialogue = ({ dialogueData, student }) => {
  const intl = useIntl();
  const chatBodyRef = React.useRef(null);

  React.useEffect(() => {
    if (chatBodyRef.current) {
      requestAnimationFrame(() => {
        chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
      });
    }
  }, [dialogueData]);

  console.log(dialogueData);
  return (
    <StyledChatCard
      title={ intl.formatMessage({ id: 'edit-student-profile.main.student-dialogue.title' })}
    >
      <ScrollContainer ref={chatBodyRef}>
        {dialogueData && dialogueData.map(message => (
          <Dialogue key={message.messageId} message={message} student={student} />
        ))}
      </ScrollContainer>
    </StyledChatCard>
  );
};

export default StudentDialogue;
